import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Grid,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from '@mui/styles';
import { FONT_FAMILY } from "../common/sharedFunctions";
import { api } from 'common';
import AdminStatusBadge from "../components/AdminStatus";
import { ActionButton, PageHeader, SectionHeader, StatusBadge, SurfaceCard } from "../components/adminPrimitives";
import adminTokens from "../styles/adminDesignTokens";

const PINNED_METHODS = ["cash", "gcash", "paymaya"];
const INTERNAL_KEYS = ["processor", "method"];
const ACTIVE_KEY = "active";
const TESTING_KEYS = ["testing", "testingMode"];
const PUBLIC_KEY_NAMES = [
    "public_key",
    "publicKey",
    "PUBLIC_KEY",
    "FLUTTERWAVE_PUBLIC_KEY",
    "PAYSTACK_PUBLIC_KEY",
    "stripe_public_key",
    "paypal_client_id",
    "KEY_ID",
    "APPLICATION_ID",
    "ACCOUNT_NO",
    "MERCHANT_CODE",
    "PAYTM_MEWRCHANT_ID",
    "PAYFAST_MEWRCHANT_ID",
    "merchantId"
];
const SECRET_KEY_NAMES = [
    "secret_key",
    "privateKey",
    "private_key",
    "SECURE_KEY",
    "FLUTTERWAVE_SECRET_KEY",
    "access_token",
    "PAYFAST_MERCHANT_KEY",
    "paypal_secret",
    "PAYSTACK_SECRET_KEY",
    "PAYTM_MERCHANT_KEY",
    "apiKey",
    "TXN_PASSWORD",
    "stripe_private_key",
    "ACCESS_TOKEN",
    "KEY_SECRET",
    "secretKey",
    "PAYFAST_MERCHANT_KEY"
];

const FALLBACK_LABELS = {
    payment_settings: "Payment Settings",
    payment_settings_help: "Manage payment methods, keys, and active status from one table.",
    payment_option: "Payment Option",
    active_status: "Active Status",
    public_key: "Public Key",
    secret_key: "Secret Key",
    testing: "Testing",
    other_settings: "Other Settings",
    submit: "Submit",
    active: "Active",
    inactive: "Inactive",
    cash: "Cash",
    gcash: "GCash",
    paymaya: "PayMaya",
    paymongo: "PayMongo"
};

const useStyles = makeStyles({
    tableCard: {
        width: "100%"
    },
    headerCell: {
        fontFamily: FONT_FAMILY,
        fontWeight: "bold",
        backgroundColor: "#F7FBFF",
        color: "#102E56",
        borderBottom: "1px solid rgba(11, 124, 255, 0.12)"
    },
    textField: {
        "& input": {
            fontFamily: FONT_FAMILY,
        },
        "& label": {
            fontFamily: FONT_FAMILY,
        }
    },
    buttonStyle: {
        marginTop: 18,
        minWidth: 250,
        height: 40,
        borderRadius: "999px",
        color: "#FFFFFF",
        fontWeight: "bold",
        fontSize: "16px",
        fontFamily: FONT_FAMILY,
    }
});

const label = (t, key) => {
    const value = t(key);
    return value === key ? (FALLBACK_LABELS[key] || key) : value;
};

const displayName = (t, method) => {
    const friendly = {
        gcash: "GCash",
        paymaya: "PayMaya",
        paymongo: "PayMongo",
        payulatam: "PayU Latam",
        payfast: "PayFast",
        paystack: "PayStack",
        paytm: "PayTM",
        paypal: "PayPal",
        braintree: "Braintree",
        flutterwave: "Flutterwave",
        mercadopago: "Mercado Pago",
        squareup: "Square",
        razorpay: "Razorpay",
        slickpay: "SlickPay",
        xendit: "Xendit",
        wipay: "WiPay",
        iyzico: "Iyzico",
        liqpay: "LiqPay",
        securepay: "SecurePay",
        stripe: "Stripe",
        culqi: "Culqi",
        test: "Test"
    };
    const translated = t(method);
    return translated === method ? (friendly[method] || method) : translated;
};

const clone = (value) => JSON.parse(JSON.stringify(value || {}));

const normalizePaymentSettings = (providers, settings) => {
    if (!providers) return null;
    const normalized = clone(providers);
    const paymongo = normalized.paymongo || {};
    const storedCash = normalized.cash || {};
    normalized.cash = {
        ...storedCash,
        active: settings && typeof settings.disable_cash === "boolean" ? !settings.disable_cash : storedCash.active !== false
    };
    normalized.gcash = {
        active: false,
        public_key: paymongo.public_key || "",
        secret_key: paymongo.secret_key || "",
        processor: "paymongo",
        method: "gcash",
        ...(normalized.gcash || {})
    };
    normalized.paymaya = {
        active: false,
        public_key: paymongo.public_key || "",
        secret_key: paymongo.secret_key || "",
        processor: "paymongo",
        method: "paymaya",
        ...(normalized.paymaya || {})
    };
    return normalized;
};

const getFirstExistingKey = (config, keys) => keys.find((key) => Object.prototype.hasOwnProperty.call(config, key));

const getPublicKeyName = (method, config) => {
    if (method === "cash") return null;
    return getFirstExistingKey(config, PUBLIC_KEY_NAMES) || "public_key";
};

const getSecretKeyName = (method, config) => {
    if (method === "cash") return null;
    return getFirstExistingKey(config, SECRET_KEY_NAMES) || "secret_key";
};

const getTestingKey = (config) => TESTING_KEYS.find((key) => Object.prototype.hasOwnProperty.call(config, key));

const isExtraField = (key, publicKeyName, secretKeyName, testingKey) => {
    return key !== ACTIVE_KEY &&
        key !== publicKeyName &&
        key !== secretKeyName &&
        key !== testingKey &&
        !INTERNAL_KEYS.includes(key);
};

export default function PaymentSettings() {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const classes = useStyles();
    const { editPaymentMethods } = api;
    const paymentmethods = useSelector(state => state.paymentmethods);
    const settings = useSelector((state) => state.settingsdata.settings);
    const dispatch = useDispatch();
    const [paymentSettings, setPaymentSettings] = useState();
    const [oldPaymentSettings, setOldPaymentSettings] = useState();

    useEffect(() => {
        if (paymentmethods.providers) {
            const normalized = normalizePaymentSettings(paymentmethods.providers, settings);
            setPaymentSettings(normalized);
            setOldPaymentSettings(clone(normalized));
        } else {
            setPaymentSettings(null);
            setOldPaymentSettings(null);
        }
    }, [paymentmethods.providers, settings]);

    const providerNames = useMemo(() => {
        if (!paymentSettings) return [];
        const keys = Object.keys(paymentSettings).filter((key) => paymentSettings[key] && typeof paymentSettings[key] === "object" && !Array.isArray(paymentSettings[key]));
        return [
            ...PINNED_METHODS.filter((key) => keys.includes(key)),
            ...keys.filter((key) => !PINNED_METHODS.includes(key)).sort((a, b) => displayName(t, a).localeCompare(displayName(t, b)))
        ];
    }, [paymentSettings, t]);

    const updateProviderField = (method, key, value) => {
        setPaymentSettings((prev) => ({
            ...prev,
            [method]: {
                ...(prev[method] || {}),
                [key]: method === 'payulatam' && (key === 'merchantId' || key === 'accountId') && value !== "" ? parseInt(value) : value
            }
        }));
    };

    const handleSubmit = () => {
        if (settings.AllowCriticalEditsAdmin) {
            dispatch(editPaymentMethods(paymentSettings));
        } else {
            alert(t("demo_mode"));
        }
    };

    const activeProviderCount = providerNames.filter((method) => paymentSettings && paymentSettings[method] && paymentSettings[method].active === true).length;
    const cashEnabled = paymentSettings && paymentSettings.cash && paymentSettings.cash.active === true;

    return (
        paymentSettings ?
            <Box sx={{ width: '100%', direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }} className={classes.tableCard}>
                <PageHeader
                    eyebrow={label(t, "finance")}
                    title={label(t, "payment_settings")}
                    description={label(t, "payment_settings_help")}
                    actions={<StatusBadge tone={cashEnabled ? "success" : "warning"}>{cashEnabled ? `${label(t, "cash")} ${label(t, "active")}` : `${label(t, "cash")} ${label(t, "inactive")}`}</StatusBadge>}
                />
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={4}>
                        <SurfaceCard sx={{ minHeight: 112 }}>
                            <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, fontWeight: 850 }}>{label(t, "payment_option")}</Typography>
                            <Typography sx={{ color: adminTokens.colors.text, fontSize: "1.9rem", fontWeight: 950 }}>{providerNames.length}</Typography>
                        </SurfaceCard>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <SurfaceCard sx={{ minHeight: 112 }}>
                            <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, fontWeight: 850 }}>{label(t, "active_status")}</Typography>
                            <Typography sx={{ color: adminTokens.colors.success, fontSize: "1.9rem", fontWeight: 950 }}>{activeProviderCount}</Typography>
                        </SurfaceCard>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <SurfaceCard sx={{ minHeight: 112 }}>
                            <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, fontWeight: 850 }}>{label(t, "testing")}</Typography>
                            <Typography sx={{ color: adminTokens.colors.purple, fontSize: "1.2rem", fontWeight: 950 }}>{providerNames.some((method) => {
                                const config = paymentSettings[method] || {};
                                const testingKey = getTestingKey(config);
                                return testingKey && config[testingKey] === true;
                            }) ? label(t, "active") : "-"}</Typography>
                        </SurfaceCard>
                    </Grid>
                </Grid>
                <SurfaceCard>
                    <SectionHeader
                        title={label(t, "payment_settings")}
                        description={label(t, "payment_settings_help")}
                    />
                <TableContainer component={Paper} sx={{ borderRadius: 4, border: `1px solid ${adminTokens.colors.border}`, boxShadow: "none", overflowX: "auto" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.headerCell}>{label(t, "payment_option")}</TableCell>
                                <TableCell className={classes.headerCell}>{label(t, "active_status")}</TableCell>
                                <TableCell className={classes.headerCell}>{label(t, "public_key")}</TableCell>
                                <TableCell className={classes.headerCell}>{label(t, "secret_key")}</TableCell>
                                <TableCell className={classes.headerCell}>{label(t, "testing")}</TableCell>
                                <TableCell className={classes.headerCell}>{label(t, "other_settings")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {providerNames.map((method) => {
                                const config = paymentSettings[method] || {};
                                const publicKeyName = getPublicKeyName(method, config);
                                const secretKeyName = getSecretKeyName(method, config);
                                const testingKey = getTestingKey(config);
                                const extraKeys = Object.keys(config).filter((key) => isExtraField(key, publicKeyName, secretKeyName, testingKey));
                                const secretUnchanged = oldPaymentSettings && oldPaymentSettings[method] && secretKeyName && config[secretKeyName] === oldPaymentSettings[method][secretKeyName];
                                return (
                                    <TableRow key={method}>
                                        <TableCell style={{ fontFamily: FONT_FAMILY, fontWeight: "bold", minWidth: 140 }}>
                                            {displayName(t, method)}
                                        </TableCell>
                                        <TableCell style={{ minWidth: 140 }}>
                                            <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                <Switch
                                                    checked={config.active === true}
                                                    onChange={(event) => updateProviderField(method, ACTIVE_KEY, event.target.checked)}
                                                    color="primary"
                                                />
                                                <AdminStatusBadge status={config.active === true ? "ACTIVE" : "INACTIVE"}>
                                                    {config.active === true ? label(t, "active") : label(t, "inactive")}
                                                </AdminStatusBadge>
                                            </Box>
                                        </TableCell>
                                        <TableCell style={{ minWidth: 220 }}>
                                            {publicKeyName ?
                                                <TextField
                                                    className={classes.textField}
                                                    size="small"
                                                    fullWidth
                                                    label={publicKeyName}
                                                    name={`${method}|${publicKeyName}`}
                                                    value={config[publicKeyName] === undefined || config[publicKeyName] === null ? "" : config[publicKeyName]}
                                                    onChange={(event) => updateProviderField(method, publicKeyName, event.target.value)}
                                                />
                                                :
                                                <Typography variant="caption" style={{ fontFamily: FONT_FAMILY }}>{t("not_required") === "not_required" ? "Not required" : t("not_required")}</Typography>
                                            }
                                        </TableCell>
                                        <TableCell style={{ minWidth: 220 }}>
                                            {secretKeyName ?
                                                <TextField
                                                    className={classes.textField}
                                                    size="small"
                                                    fullWidth
                                                    type={secretUnchanged ? "password" : "text"}
                                                    label={secretKeyName}
                                                    name={`${method}|${secretKeyName}`}
                                                    value={config[secretKeyName] === undefined || config[secretKeyName] === null ? "" : config[secretKeyName]}
                                                    onFocus={() => {
                                                        if (secretUnchanged) {
                                                            updateProviderField(method, secretKeyName, "");
                                                        }
                                                    }}
                                                    onChange={(event) => updateProviderField(method, secretKeyName, event.target.value)}
                                                />
                                                :
                                                <Typography variant="caption" style={{ fontFamily: FONT_FAMILY }}>{t("not_required") === "not_required" ? "Not required" : t("not_required")}</Typography>
                                            }
                                        </TableCell>
                                        <TableCell style={{ minWidth: 110 }}>
                                            {testingKey ?
                                                <Switch
                                                    checked={config[testingKey] === true}
                                                    onChange={(event) => updateProviderField(method, testingKey, event.target.checked)}
                                                    color="primary"
                                                />
                                                :
                                                <Typography variant="caption" style={{ fontFamily: FONT_FAMILY }}>-</Typography>
                                            }
                                        </TableCell>
                                        <TableCell style={{ minWidth: 260 }}>
                                            {extraKeys.length > 0 ?
                                                <Grid container spacing={1}>
                                                    {extraKeys.map((key) => (
                                                        <Grid item xs={12} md={6} key={`${method}-${key}`}>
                                                            {typeof config[key] === "boolean" ?
                                                                <Box style={{ display: "flex", alignItems: "center" }}>
                                                                    <Switch
                                                                        checked={config[key] === true}
                                                                        onChange={(event) => updateProviderField(method, key, event.target.checked)}
                                                                        color="primary"
                                                                    />
                                                                    <Typography variant="caption" style={{ fontFamily: FONT_FAMILY }}>{key}</Typography>
                                                                </Box>
                                                                :
                                                                <TextField
                                                                    className={classes.textField}
                                                                    size="small"
                                                                    fullWidth
                                                                    label={key}
                                                                    value={config[key] === undefined || config[key] === null ? "" : config[key]}
                                                                    onChange={(event) => updateProviderField(method, key, event.target.value)}
                                                                />
                                                            }
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                                :
                                                <Typography variant="caption" style={{ fontFamily: FONT_FAMILY }}>-</Typography>
                                            }
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <ActionButton
                    size="large"
                    onClick={handleSubmit}
                    className={classes.buttonStyle}
                >
                    {label(t, "submit")}
                </ActionButton>
                </SurfaceCard>
            </Box>
            : null
    );
}
